import { type SessionStatus, SESSION_STATUSES } from '@sbc/domain-models/agents';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAgents } from '@/lib/api/agents';
import { useSessions } from '@/lib/api/sessions';

import { OriginBadge } from './origin-badge';
import { buildTree, type SessionTreeNode } from './session-tree';
import { StatusBadge } from './status-badge';

const PAGE_SIZE = 20;

// Radix Select has no empty-string item value, so the "no filter" choice needs a
// sentinel; '' stays the state's canonical "unfiltered".
const ALL_FILTER = 'all';

export function SessionsListPage() {
  const [search, setSearch] = useState('');
  const [agentId, setAgentId] = useState('');
  const [status, setStatus] = useState<SessionStatus | ''>('');
  const [page, setPage] = useState(1);

  // Populate the agent filter; the list is small in Phase 3.
  const agentsQuery = useAgents({ page: 1, pageSize: 100 });

  const sessionsQuery = useSessions({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
    agentId: agentId || undefined,
    status: status || undefined,
  });

  const resetPage = () => setPage(1);
  const result = sessionsQuery.data;
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  const tree = useMemo(() => buildTree(result?.items ?? []), [result]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const renderNodes = (nodes: SessionTreeNode[], depth: number): ReactNode[] =>
    nodes.flatMap((node) => {
      const isCollapsed = collapsed.has(node.id);
      const hasChildren = node.children.length > 0;
      const row = (
        <TableRow key={node.id}>
          <TableCell>
            <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 1.25}rem` }}>
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => toggle(node.id)}
                  className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                  aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isCollapsed ? (
                    <ChevronRight className="size-4" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </button>
              ) : (
                <span className="inline-block size-4" />
              )}
              <Link
                to="/sessions/$sessionId"
                params={{ sessionId: node.id }}
                className="font-medium hover:underline"
              >
                {node.name ?? 'Untitled session'}
              </Link>
              {node.originKind && <OriginBadge kind={node.originKind} />}
            </div>
          </TableCell>
          <TableCell className="text-sm">
            {node.agentName} · v{node.agentVersionNumber}
          </TableCell>
          <TableCell>
            <StatusBadge status={node.status} />
          </TableCell>
          <TableCell className="text-right text-sm tabular-nums">
            <div>
              {node.tokensIn} / {node.tokensOut}
            </div>
            {node.tokensIn > 0 && (
              <div className="text-xs text-[var(--color-muted-foreground)]">
                {node.cacheReadTokens} cached
              </div>
            )}
          </TableCell>
          <TableCell className="text-right text-sm tabular-nums">{node.messageCount}</TableCell>
          <TableCell className="text-sm">{new Date(node.createdAt).toLocaleString()}</TableCell>
        </TableRow>
      );
      const childRows = hasChildren && !isCollapsed ? renderNodes(node.children, depth + 1) : [];
      return [row, ...childRows];
    });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sessions</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Trace and test conversations, each pinned to one agent version.
          </p>
        </div>
        <Button asChild>
          <Link to="/sessions/new">
            <Plus className="size-4" />
            New session
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by name or id…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            resetPage();
          }}
          className="h-8 w-full sm:w-64"
        />
        <Select
          value={agentId || ALL_FILTER}
          onValueChange={(value) => {
            setAgentId(value === ALL_FILTER ? '' : value);
            resetPage();
          }}
        >
          <SelectTrigger aria-label="Filter by agent" className="h-8 w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER}>All agents</SelectItem>
            {agentsQuery.data?.items.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>
                {agent.activeVersion.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status || ALL_FILTER}
          onValueChange={(value) => {
            const next = SESSION_STATUSES.find((candidate) => candidate === value);
            setStatus(next ?? '');
            resetPage();
          }}
        >
          <SelectTrigger aria-label="Filter by status" className="h-8 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER}>All statuses</SelectItem>
            {SESSION_STATUSES.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {sessionsQuery.isPending && <p className="text-sm">Loading…</p>}
      {sessionsQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load sessions: {sessionsQuery.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Tokens in/out</TableHead>
                  <TableHead className="text-right">Messages</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tree.length ? (
                  renderNodes(tree, 0)
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      No sessions yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {result.total} session(s)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => value + 1)}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
